import React, { useState, useEffect } from 'react';
import StorageManager from '../services/StorageManager';
import { StarIcon, AnimatedFlameIcon } from '../components/Icons';

function CreatorPortal({ currentUser, pilots, onHome, onUploadNew, onSelectPilot, onRefreshPilots, onUserUpdate, onLogout }) {
  const [myPilots, setMyPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState({});
  const [feedbackLoading, setFeedbackLoading] = useState({});
  const [pilotVotes, setPilotVotes] = useState({});
  const [pilotComments, setPilotComments] = useState({});
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [hoveredNav, setHoveredNav] = useState(null);

  // Messages inbox
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Profile modal
  const getDefaultAboutMe = () => {
    if (currentUser.aboutMe) return currentUser.aboutMe;
    try {
      const app = typeof currentUser.creatorApplication === 'string'
        ? JSON.parse(currentUser.creatorApplication) : currentUser.creatorApplication;
      return app?.describes || '';
    } catch (e) { return ''; }
  };

  const [profileData, setProfileData] = useState({
    profilePicture: currentUser.profilePicture || '',
    displayName: currentUser.displayName || '',
    aboutMe: getDefaultAboutMe()
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  const loadPilots = async () => {
    try {
      const p = await StorageManager.getMyPilots(currentUser.id);
      setMyPilots(p || []);
    } catch (err) { console.error('Error loading pilots:', err); }
    setLoading(false);
  };

  const loadMessages = async () => {
    try {
      const msgs = await StorageManager.getMessagesForCreator(currentUser.id);
      setMessages(msgs || []);
      setUnreadCount((msgs || []).filter(m => !m.isRead).length);
    } catch (err) { console.error('Error loading messages:', err); }
    setMessagesLoading(false);
  };

  useEffect(() => { loadPilots(); loadMessages(); }, [currentUser.id]);

  const handleProfileSave = async () => {
    setProfileSaving(true); setProfileError(''); setProfileSuccess(false);
    const result = await StorageManager.updateCreatorProfile(currentUser.id, profileData);
    if (result.success) {
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
      if (onUserUpdate) {
        const updatedUser = StorageManager.getCurrentVoter();
        if (updatedUser) onUserUpdate(updatedUser);
      }
    } else { setProfileError(result.message || 'Failed to save profile'); }
    setProfileSaving(false);
  };

  const toggleFeedback = async (pilotId) => {
    if (expandedFeedback[pilotId]) {
      setExpandedFeedback(prev => ({ ...prev, [pilotId]: false }));
    } else {
      setExpandedFeedback(prev => ({ ...prev, [pilotId]: true }));
      if (!pilotVotes[pilotId]) {
        setFeedbackLoading(prev => ({ ...prev, [pilotId]: true }));
        try {
          const votes = await StorageManager.getVotesForPilot(pilotId);
          setPilotVotes(prev => ({ ...prev, [pilotId]: votes || [] }));
          // Load comments (messages) for this pilot
          const msgs = await StorageManager.getMessagesForCreator(currentUser.id);
          const pilotMsgs = (msgs || []).filter(m => m.pilotId === pilotId);
          setPilotComments(prev => ({ ...prev, [pilotId]: pilotMsgs }));
        } catch (err) {
          setPilotVotes(prev => ({ ...prev, [pilotId]: [] }));
          setPilotComments(prev => ({ ...prev, [pilotId]: [] }));
        }
        setFeedbackLoading(prev => ({ ...prev, [pilotId]: false }));
      }
    }
  };

  const computeAggregate = (votes) => {
    if (!votes || votes.length === 0) return null;
    const total = votes.length;
    const avgOverall = votes.reduce((s, v) => s + (v.overallScore || 0), 0) / total;
    const curiosityVotes = votes.filter(v => v.curiosityScore > 0);
    const avgCuriosity = curiosityVotes.length > 0 ? curiosityVotes.reduce((s, v) => s + v.curiosityScore, 0) / curiosityVotes.length : 0;
    // Count pull factors for tug-of-war
    const pullInCounts = {};
    const pullBackCounts = {};
    const allFactors = new Set();
    votes.forEach(v => {
      (v.pullFactorsIn || []).forEach(f => { pullInCounts[f] = (pullInCounts[f] || 0) + 1; allFactors.add(f); });
      (v.pullFactorsBack || []).forEach(f => { pullBackCounts[f] = (pullBackCounts[f] || 0) + 1; allFactors.add(f); });
    });
    // Build tug-of-war data: each factor has inCount and backCount
    const tugOfWar = Array.from(allFactors).map(f => ({
      factor: f,
      inCount: pullInCounts[f] || 0,
      backCount: pullBackCounts[f] || 0,
      total: (pullInCounts[f] || 0) + (pullBackCounts[f] || 0)
    })).sort((a, b) => b.total - a.total);
    return { total, avgOverall, avgCuriosity, tugOfWar };
  };

  const handleExpandMessage = async (msg) => {
    if (expandedMessage === msg.id) { setExpandedMessage(null); return; }
    setExpandedMessage(msg.id);
    if (!msg.isRead) {
      await StorageManager.markMessageRead(msg.id);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const navStyle = (id) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    color: hoveredNav === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.9rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    transition: 'color 0.25s ease', letterSpacing: '0.05em', padding: '0.3rem 0'
  });

  const ghostBtn = (id, disabled) => ({
    background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px',
    padding: '0.7rem 1.8rem', color: hoveredBtn === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '1rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.25s ease', letterSpacing: '0.06em',
    borderColor: hoveredBtn === id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
    opacity: disabled ? 0.5 : 1
  });

  const sectionStyle = {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem'
  };

  const inputStyle = {
    width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff',
    fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', fontFamily: "'Cormorant Garamond', Georgia, serif"
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#000000', fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      ` }} />

      {/* Profile Modal */}
      {showProfileModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
          onClick={() => setShowProfileModal(false)}>
          <div style={{ background: '#000000', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '2rem', maxWidth: '480px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '1rem', fontWeight: 300, letterSpacing: '0.1em', marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>EDIT PROFILE</h2>

            {profileError && <div style={{ padding: '0.8rem', background: 'rgba(255,107,107,0.15)', borderRadius: '8px', color: '#ff6b6b', marginBottom: '1rem', fontSize: '0.82rem' }}>{profileError}</div>}
            {profileSuccess && <div style={{ padding: '0.8rem', background: 'rgba(78,205,196,0.15)', borderRadius: '8px', color: '#4ecdc4', marginBottom: '1rem', fontSize: '0.82rem' }}>Profile updated.</div>}

            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>Display Name</label>
              <input type="text" value={profileData.displayName} onChange={e => setProfileData({ ...profileData, displayName: e.target.value })}
                style={inputStyle} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>About Me</label>
              <textarea value={profileData.aboutMe} onChange={e => setProfileData({ ...profileData, aboutMe: e.target.value })}
                style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowProfileModal(false)} style={ghostBtn('cancel-profile', false)}
                onMouseEnter={() => setHoveredBtn('cancel-profile')} onMouseLeave={() => setHoveredBtn(null)}>Cancel</button>
              <button onClick={handleProfileSave} disabled={profileSaving} style={ghostBtn('save-profile', profileSaving)}
                onMouseEnter={() => setHoveredBtn('save-profile')} onMouseLeave={() => setHoveredBtn(null)}>
                {profileSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '1rem', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pilot Light</span>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <button onClick={() => setShowProfileModal(true)} style={navStyle('profile')}
            onMouseEnter={() => setHoveredNav('profile')} onMouseLeave={() => setHoveredNav(null)}>Profile</button>
          <button onClick={onLogout} style={navStyle('logout')}
            onMouseEnter={() => setHoveredNav('logout')} onMouseLeave={() => setHoveredNav(null)}>Log Out</button>
        </nav>
      </header>

      <div style={{ flex: 1, padding: '2rem 1.5rem', maxWidth: '800px', margin: '0 auto', width: '100%' }}>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '2rem', animation: 'fadeInUp 0.5s ease-out' }}>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 300, letterSpacing: '0.12em',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CREATOR PORTAL</h1>
        </div>


        {/* My Pilots */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <AnimatedFlameIcon size={36} style={{ margin: '0 auto', display: 'block' }} />
            <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: '1rem', fontSize: '0.85rem' }}>Loading your pilots...</p>
          </div>
        ) : myPilots.length === 0 ? (
          <div style={{ ...sectionStyle, textAlign: 'center', padding: '2.5rem' }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem' }}>No pilots to display.</p>
          </div>
        ) : (
          <div style={{ animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', letterSpacing: '0.04em' }}>My Pilots</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {myPilots.map(pilot => {
                return (
                  <div key={pilot.id} style={{ ...sectionStyle, marginBottom: 0 }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      {/* Thumbnail */}
                      <div style={{ width: '120px', height: '68px', borderRadius: '8px', flexShrink: 0, overflow: 'hidden', cursor: 'pointer',
                        background: pilot.playbackId
                          ? `url(https://image.mux.com/${pilot.playbackId}/thumbnail.png?width=240&height=136&fit_mode=smartcrop) center/cover`
                          : 'linear-gradient(135deg, rgba(255,107,107,0.15), rgba(78,205,196,0.15))' }}
                        onClick={() => onSelectPilot(pilot)} />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: 500, margin: '0 0 0.3rem', color: 'rgba(255,255,255,0.85)' }}>{pilot.pilotTitle}</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
                          {pilot.genre && <span>{pilot.genre}</span>}
                          {pilot.totalVotes > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <StarIcon size={12} style={{ color: '#feca57' }} /> {pilot.avgOverall?.toFixed(1) || '—'} ({pilot.totalVotes})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Feedback toggle */}
                    <button onClick={() => toggleFeedback(pilot.id)}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem',
                        cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", padding: '0.5rem 0 0', transition: 'color 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                      onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>
                      {expandedFeedback[pilot.id] ? 'Hide Feedback' : 'View Feedback'}
                    </button>

                    {expandedFeedback[pilot.id] && (
                      <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        {feedbackLoading[pilot.id] ? (
                          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', textAlign: 'center' }}>Loading...</p>
                        ) : (() => {
                          const votes = pilotVotes[pilot.id] || [];
                          const cmts = pilotComments[pilot.id] || [];
                          const agg = computeAggregate(votes);
                          if (!agg) return <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', textAlign: 'center' }}>No feedback yet</p>;
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {/* Aggregate stats */}
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
                                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '0.8rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.04)' }}>
                                  <div style={{ fontSize: '1.2rem', fontWeight: 500, color: '#feca57', marginBottom: '0.15rem' }}>{agg.avgOverall.toFixed(1)}</div>
                                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>Avg Rating</div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '0.8rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.04)' }}>
                                  <div style={{ fontSize: '1.2rem', fontWeight: 500, color: 'rgba(255,107,107,0.8)', marginBottom: '0.15rem' }}>{agg.avgCuriosity > 0 ? agg.avgCuriosity.toFixed(1) : '—'}</div>
                                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>Curiosity</div>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '0.8rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.04)' }}>
                                  <div style={{ fontSize: '1.2rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', marginBottom: '0.15rem' }}>{agg.total}</div>
                                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>Total Votes</div>
                                </div>
                              </div>

                              {/* Pull factors — tug-of-war bars */}
                              {agg.tugOfWar.length > 0 && (
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.68rem', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                                    <span style={{ color: '#4ecdc4' }}>Pulled In</span>
                                    <span style={{ color: '#ff6b6b' }}>Held Back</span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                    {agg.tugOfWar.map(({ factor, inCount, backCount, total }) => {
                                      const inPct = total > 0 ? (inCount / total) * 100 : 50;
                                      return (
                                        <div key={factor}>
                                          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginBottom: '3px', textAlign: 'center',
                                            fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{factor}</div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.65rem', color: 'rgba(78,205,196,0.7)', width: '16px', textAlign: 'right', flexShrink: 0 }}>{inCount}</span>
                                            <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.04)', overflow: 'hidden',
                                              display: 'flex', position: 'relative' }}>
                                              <div style={{ width: `${inPct}%`, height: '100%',
                                                background: 'linear-gradient(90deg, rgba(78,205,196,0.15), rgba(78,205,196,0.5))',
                                                borderRadius: '3px 0 0 3px', transition: 'width 0.4s ease' }} />
                                              <div style={{ width: `${100 - inPct}%`, height: '100%',
                                                background: 'linear-gradient(90deg, rgba(255,107,107,0.5), rgba(255,107,107,0.15))',
                                                borderRadius: '0 3px 3px 0', transition: 'width 0.4s ease' }} />
                                            </div>
                                            <span style={{ fontSize: '0.65rem', color: 'rgba(255,107,107,0.7)', width: '16px', textAlign: 'left', flexShrink: 0 }}>{backCount}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Comments section */}
                              {cmts.length > 0 && (
                                <div>
                                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.4rem', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                                    Comments ({cmts.length})
                                  </div>
                                  <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                                    paddingRight: '0.3rem' }}>
                                    {cmts.map((c, idx) => (
                                      <div key={idx} style={{ padding: '0.6rem', background: 'rgba(255,255,255,0.02)', borderRadius: '6px',
                                        border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', lineHeight: 1.5,
                                          fontFamily: "'Cormorant Garamond', Georgia, serif" }}>"{c.messageText}"</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ padding: '2.5rem 2rem', textAlign: 'center', marginTop: 'auto' }}>
        <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.75rem', margin: 0, letterSpacing: '0.04em' }}>
          © 2026 Pilot Light. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default CreatorPortal;
